import { useEffect, useRef, useState } from "react";
import type { DeepfakeGenConfig } from "../scenario/types";
import { audio } from "../lib/audio";

/**
 * The hacker's "deepfake studio" cinematic. Over the dark hacker-room laptop, a
 * fake desktop app ingests the photo + voiceprint stolen at the restaurant and
 * trains a face/voice model with a progress bar, ending in a "ready" preview.
 * Pure DOM; no real model runs. Shows the learner HOW little the attacker needed.
 */
export function DeepfakeGen({ config, onContinue }: { config: DeepfakeGenConfig; onContinue: () => void }) {
  const steps = config.steps ?? [
    "Importing source photo",
    "Detecting & aligning face landmarks",
    "Extracting voiceprint from captured audio",
    "Training face + voice model",
    "Rendering deepfake preview",
  ];
  const [stepIdx, setStepIdx] = useState(-1);
  const [progress, setProgress] = useState(0);
  const [ready, setReady] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    if (config.line) audio.speak(config.line, { voice: config.voiceId || "onyx", lang: config.language || "en-US" });
    const timers: number[] = [];
    // advance steps
    steps.forEach((_, i) => {
      timers.push(window.setTimeout(() => mounted.current && setStepIdx(i), 700 + i * 1400));
    });
    // progress bar 0 -> 100 over the same window
    const total = 700 + steps.length * 1400 + 600;
    const t0 = performance.now();
    const tick = window.setInterval(() => {
      const k = Math.min(1, (performance.now() - t0) / total);
      if (mounted.current) setProgress(Math.round(k * 100));
      if (k >= 1) {
        window.clearInterval(tick);
        if (mounted.current) {
          setStepIdx(steps.length);
          setReady(true);
        }
      }
    }, 80);
    timers.push(tick);
    return () => {
      mounted.current = false;
      audio.cancelSpeech();
      timers.forEach((t) => {
        window.clearTimeout(t);
        window.clearInterval(t);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="dfgen">
      <div className="dfgen-app">
          <div className="dfgen-titlebar">
            <span className="dfgen-dot r" />
            <span className="dfgen-dot y" />
            <span className="dfgen-dot g" />
            <span className="dfgen-app-name">{config.appName ?? "FaceForge AI — Studio"}</span>
            <span className="dfgen-badge">offline build</span>
          </div>

          <div className="dfgen-body">
            <div className="dfgen-left">
              <div className="dfgen-card">
                <div className="dfgen-card-h">Source identity</div>
                <div className="dfgen-photo">
                  {config.sourcePhotoUrl ? (
                    <img src={config.sourcePhotoUrl} alt="" />
                  ) : (
                    <div className="dfgen-photo-ph" />
                  )}
                  <span className="dfgen-scan-line" />
                  <span className="dfgen-mesh" />
                </div>
                <div className="dfgen-photo-name">{config.sourceName ?? "captured_target.jpg"}</div>
              </div>

              <div className="dfgen-card">
                <div className="dfgen-card-h">Voiceprint</div>
                <div className="dfgen-wave">
                  {Array.from({ length: 40 }).map((_, i) => (
                    <span key={i} style={{ animationDelay: `${(i % 10) * 0.08}s` }} />
                  ))}
                </div>
                <div className="dfgen-photo-name">captured_audio.wav · 0:46</div>
              </div>
            </div>

            <div className="dfgen-right">
              <div className="dfgen-card-h">Pipeline</div>
              <ul className="dfgen-steps">
                {steps.map((s, i) => {
                  const state = i < stepIdx || ready ? "done" : i === stepIdx ? "active" : "wait";
                  return (
                    <li key={i} className={`dfgen-step ${state}`}>
                      <span className="dfgen-step-ico">
                        {state === "done" ? "\u2713" : state === "active" ? "" : ""}
                      </span>
                      {s}
                    </li>
                  );
                })}
              </ul>

              <div className="dfgen-progress">
                <div className="dfgen-progress-bar" style={{ width: `${progress}%` }} />
              </div>
              <div className="dfgen-progress-label">
                {ready ? "Deepfake ready" : `Generating deepfake… ${progress}%`}
              </div>

              {ready && (
                <div className="dfgen-preview">
                  <div className="dfgen-preview-h">
                    <span className="dfgen-live-dot" /> Preview — indistinguishable from real
                  </div>
                  <div className="dfgen-preview-tile">
                    {config.sourcePhotoUrl && <img src={config.sourcePhotoUrl} alt="" />}
                    <span className="dfgen-play">{"\u25B6"}</span>
                  </div>
                  <button className="btn dfgen-continue" onClick={onContinue}>
                    {config.continueLabel ?? "What happens next?"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
  );
}
